import React, { useState, useEffect, useMemo } from 'react';
import { reportHandledError } from '../utils/sentry';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import LocalizedNumberInput from '../components/ui/LocalizedNumberInput';
import AccountReconciliationModal from '../components/AccountReconciliationModal';
import { formatCurrencyAr, parseLocalizedNumber } from '../utils/numberFormat';
import { buildLocalOperationTimestamp } from '../utils/localDateTime';
import {
  applyCollectionAllocations,
  applyCollectionToReceivables,
  fetchOpenClientReceivables,
  revertCollectionReceivableApplications,
} from '../utils/salesReceivables';
import {
  buildSuggestedAllocations,
  clampMoney,
  normalizeMoney,
  sumAllocationAmounts,
} from '../utils/accountReconciliation';
import {
  fetchActiveOrganizationClientsWithBalances,
  fetchClientTimelineBalanceSnapshot,
  getClientBalanceValue,
  updateClientBalance,
} from '../utils/clientBalances';
import { persistSelectedSiteId, resolvePreferredSiteId } from '../utils/sitePreferences';
import { 
  Check, Search, ArrowDownCircle, RefreshCcw, 
  FileText, Trash2, Printer, AlertCircle,
  Eye, EyeOff, X, DollarSign, Activity, Calendar
} from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Gestión de Cobranzas a Clientes - Estándar Impulza Enterprise
 * Integrado con Libro Diario Financiero e Impresión de Recibos
 * Refactorizado al estilo Productos.jsx
 */
function TesoreriaCobranzas() {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  // Estado para controlar la anulación
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [processingDeleteId, setProcessingDeleteId] = useState(null);
  
  // Filtro de eliminados
  const [mostrarEliminados, setMostrarEliminados] = useState(false);

  const [clientes, setClientes] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [sitios, setSitios] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [conciliationOpen, setConciliationOpen] = useState(false);
  const [preparingConciliation, setPreparingConciliation] = useState(false);
  const [conciliationDocs, setConciliationDocs] = useState([]);
  const [conciliationContext, setConciliationContext] = useState(null);

  // Filtros de Auditoría (Hoy a Hoy)
  const today = new Date().toISOString().split('T')[0];
  const [fechaDesde, setFechaDesde] = useState(today);
  const [fechaHasta, setFechaHasta] = useState(today);
  const [filtroSitio, setFiltroSitio] = useState('');

  const [form, setForm] = useState({ 
    cliente_id: '', 
    bank_id: '', 
    sitio_id: '', 
    amount: '', 
    obs: '', 
    fecha: today 
  });

  useEffect(() => {
    if (profile?.organization_id) {
      loadMasters();
      loadHistorial();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, fechaDesde, fechaHasta, mostrarEliminados]);

  // Selección automática desde redirección
  useEffect(() => {
    if (location.state?.clientId && clientes.length > 0) {
      setForm(prev => ({ ...prev, cliente_id: location.state.clientId }));
    }
  }, [location.state, clientes]);

  useEffect(() => {
    const siteId = form.sitio_id || filtroSitio;
    if (siteId) persistSelectedSiteId(siteId);
  }, [form.sitio_id, filtroSitio]);

  const resetCobroForm = () => {
    const preferredSiteId = resolvePreferredSiteId(sitios, {
      currentSiteId: form.sitio_id || filtroSitio,
    });
    setForm({ cliente_id: '', bank_id: '', sitio_id: preferredSiteId, amount: '', obs: '', fecha: today });
  };

  const closeConciliationModal = (force = false) => {
    if (saving && !force) return;
    setConciliationOpen(false);
    setConciliationDocs([]);
    setConciliationContext(null);
    setPreparingConciliation(false);
  };

  const formatConciliationDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
    return date.toLocaleDateString('es-AR', { timeZone: 'UTC' });
  };

  const handleConciliationAllocationChange = (documentId, rawValue) => {
    setConciliationDocs((prev) =>
      prev.map((document) =>
        document.id === documentId
          ? {
              ...document,
              allocation: clampMoney(rawValue, document.open_amount),
            }
          : document,
      ),
    );
  };

  const persistCobro = async ({ submission, allocations = [] }) => {
    setSaving(true);
    try {
      const orgId = profile.organization_id;
      const monto = Number(submission?.monto || 0);
      const cli = clientes.find((item) => item.id === submission.cliente_id);
      const banco = bancos.find((item) => item.id === submission.bank_id);
      // Timestamp con offset LOCAL (AR), no Z falso: antes se pegaba la hora local
      // con sufijo Z (UTC) -> el movimiento caía 3 h antes y podía saltar de día. Bug T2.
      const operationTimestamp = buildLocalOperationTimestamp(submission.fecha);

      // Asiento PRIMERO (sin saldo_resultante), luego ajuste ATÓMICO del banco vía
      // RPC y recién ahí se completa el saldo. Evita mover el saldo sin asiento
      // (plata fantasma) y el lost update del read-modify-write anterior. Bug T3.
      const { data: financialMovement, error: financialMovementError } = await supabase
        .from('movimientos_financieros')
        .insert([
          {
            organization_id: orgId,
            bank_id: submission.bank_id,
            sitio_id: submission.sitio_id,
            tipo: 'Ingreso',
            categoria: 'Cobro',
            monto,
            saldo_resultante: null,
            entidad_tipo: 'Cliente',
            entidad_id: submission.cliente_id,
            descripcion: `Cobro: ${cli?.full_name || 'Cliente'}. ${submission.obs || ''}`.trim(),
            fecha_operacion: operationTimestamp,
            user_id: profile?.user_id || null,
            is_active: true,
          },
        ])
        .select('id')
        .single();

      if (financialMovementError) throw financialMovementError;

      const { data: nuevoSaldoBanco, error: adjustBankError } = await supabase.rpc('adjust_bank_balance', {
        p_bank_id: submission.bank_id,
        p_delta: monto,
      });
      if (adjustBankError) throw adjustBankError;

      await supabase.from('movimientos_financieros')
        .update({ saldo_resultante: nuevoSaldoBanco })
        .eq('id', financialMovement.id);

      const { data: clientSnapshot, error: clientSnapshotError } = await fetchClientTimelineBalanceSnapshot({
        clientId: submission.cliente_id,
        organizationId: orgId,
        supabaseClient: supabase,
      });
      if (clientSnapshotError) throw clientSnapshotError;

      const nuevoSaldoCli = getClientBalanceValue(clientSnapshot || cli) - monto;
      const { error: updateClientError } = await updateClientBalance({
        clientId: submission.cliente_id,
        nextBalance: nuevoSaldoCli,
        supabaseClient: supabase,
      });
      if (updateClientError) throw updateClientError;

      await supabase.from('client_movements').insert([
        {
          client_id: submission.cliente_id,
          organization_id: orgId,
          type: 'Cobranza',
          amount: -monto,
          current_balance: nuevoSaldoCli,
          observations: `Cobro Tesoreria: ${banco?.name || 'Caja'}. ${submission.obs || ''}`.trim(),
        },
      ]);

      const normalizedAllocations = (Array.isArray(allocations) ? allocations : [])
        .map((document) => ({
          receivable_id: document.id,
          amount: Math.max(0, normalizeMoney(document.allocation)),
        }))
        .filter((allocation) => allocation.receivable_id && allocation.amount > 0);

      if (normalizedAllocations.length > 0) {
        await applyCollectionAllocations({
          financialMovementId: financialMovement?.id,
          allocations: normalizedAllocations,
        });
      }

      closeConciliationModal(true);
      setIsSuccess(true);
      toast.success('COBRANZA REGISTRADA');

      setTimeout(() => {
        resetCobroForm();
        setIsSuccess(false);
        setSaving(false);
        loadMasters();
        loadHistorial();
      }, 1500);
    } catch (e) {
      console.error(e);
      toast.error('Error al procesar el ingreso');
      setSaving(false);
    }
  };

  async function loadMasters() {
    const orgId = profile.organization_id;
    try {
      const [c, b, s] = await Promise.all([
          fetchActiveOrganizationClientsWithBalances({ organizationId: orgId, supabaseClient: supabase }),
          supabase.from('banks').select('*').eq('organization_id', orgId).eq('is_active', true).order('name'),
          supabase.from('sitios').select('*').eq('organization_id', orgId).eq('is_active', true).order('nombre')
      ]);
      if (c.error) throw c.error;
      setClientes(c.data || []);
      setBancos(b.data || []);
      const availableSites = s.data || [];
      const preferredSiteId = resolvePreferredSiteId(availableSites, {
        currentSiteId: form.sitio_id || filtroSitio,
      });
      setSitios(availableSites);
      setForm((prev) => ({
        ...prev,
        sitio_id: resolvePreferredSiteId(availableSites, {
          currentSiteId: prev.sitio_id,
          fallbackSiteId: preferredSiteId,
        }),
      }));
      setFiltroSitio((prev) =>
        resolvePreferredSiteId(availableSites, {
          currentSiteId: prev,
          fallbackSiteId: preferredSiteId,
        }),
      );
    } catch (err) { console.error(err); }
  }

  async function loadHistorial() {
    setLoading(true);
    const orgId = profile.organization_id;
    try {
      const start = `${fechaDesde}T00:00:00.000Z`;
      const end = `${fechaHasta}T23:59:59.999Z`;

      let query = supabase
        .from('movimientos_financieros')
        .select('*, banks(name), sitios(nombre)')
        .eq('organization_id', orgId)
        .eq('categoria', 'Cobro')
        .gte('fecha_operacion', start)
        .lte('fecha_operacion', end)
        .order('created_at', { ascending: false });

      if (!mostrarEliminados) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;

      if (error) throw error;
      setHistorial(data || []);
    } catch (err) { 
      reportHandledError(err, { feature: 'tesoreria:cobranzas-libro' });
      toast.error("Error al sincronizar libro diario");
    } finally {
      setLoading(false);
    }
  }

  // Función para anular (Soft Delete con reversión de saldos)
  const anularMovimiento = async (mov) => {
    setProcessingDeleteId(mov.id);
    const orgId = profile.organization_id;
    const originalAmount = Number(mov.monto || 0);

    try {
      await revertCollectionReceivableApplications(mov.id);

      // 1. Revertir saldo Banco (Restar el cobro erróneo) de forma ATÓMICA.
      // Antes era read-modify-write: si la lectura fallaba, el saldo quedaba en
      // -originalAmount, y dos anulaciones concurrentes se pisaban. Bug T4.
      const { error: bankAdjustError } = await supabase.rpc('adjust_bank_balance', {
        p_bank_id: mov.bank_id,
        p_delta: -originalAmount,
      });
      if (bankAdjustError) throw bankAdjustError;

      // 2. Revertir saldo Cliente (Volver a sumar la deuda)
      if (mov.entidad_id) {
        const { data: clientData, error: clientError } = await fetchClientTimelineBalanceSnapshot({
          clientId: mov.entidad_id,
          organizationId: orgId,
          supabaseClient: supabase,
        });
        if (clientError) throw clientError;

        const currentClientBalance = getClientBalanceValue(clientData);
        const restoredClientBalance = currentClientBalance + originalAmount;

        const { error: updateClientError } = await updateClientBalance({
          clientId: mov.entidad_id,
          nextBalance: restoredClientBalance,
          supabaseClient: supabase,
        });
        if (updateClientError) throw updateClientError;
      }

      // 3. Soft Delete del Movimiento
      const { error } = await supabase.from('movimientos_financieros')
        .update({ 
          is_active: false, 
          monto: 0,
          descripcion: `(ANULADO) ${mov.descripcion}` 
        })
        .eq('id', mov.id);

      if (error) throw error;

      // 4. Registrar la anulación en la cuenta corriente del cliente
      if (mov.entidad_id) {
        const { data: currentClientData, error: currentClientError } = await fetchClientTimelineBalanceSnapshot({
          clientId: mov.entidad_id,
          organizationId: orgId,
          supabaseClient: supabase,
        });
        if (currentClientError) throw currentClientError;

        const currentBalance = getClientBalanceValue(currentClientData);
        await supabase.from('client_movements').insert([{
            client_id: mov.entidad_id, 
            organization_id: orgId, 
            type: 'Anulación de Cobranza',
            amount: originalAmount, // Positivo porque vuelve a sumar deuda
            current_balance: currentBalance,
            observations: `Anulación de Cobro Tesorería. Saldo restituido.`
        }]);
      }

      // Mensaje con HTML Inline (Toast)
      toast.custom((t) => (
        <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
          <div className="flex-1 w-0 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <Trash2 className="h-10 w-10 text-rose-500" />
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-900">Cobro Anulado</p>
                <p className="mt-1 text-sm text-gray-500">
                  Se restó el dinero del banco y se restableció la deuda al cliente.
                </p>
              </div>
            </div>
          </div>
        </div>
      ));
      
      setConfirmDeleteId(null);
      setExpandedId(null);
      loadMasters();
      loadHistorial(); 

    } catch (error) {
      console.error(error);
      toast.error("Error crítico al anular. Revise consola.");
    } finally {
      setProcessingDeleteId(null);
    }
  };

  const guardarCobroLegacy = async (e) => {
    e.preventDefault();
    if (!form.cliente_id || !form.bank_id || !form.sitio_id || !form.amount) {
      return toast.error("Complete todos los campos obligatorios");
    }
    
    setSaving(true);
    try {
        const orgId = profile.organization_id;
        const monto = parseLocalizedNumber(form.amount);
        if (!monto || monto <= 0) {
          toast.error("Ingresá un monto válido");
          setSaving(false);
          return;
        }
        const cli = clientes.find(item => item.id === form.cliente_id);
        const banco = bancos.find(item => item.id === form.bank_id);

        const nuevoSaldoBanco = (Number(banco.current_balance) || 0) + monto;

        // 1. Actualizar Saldo Real del Banco (Ingreso)
        await supabase.from('banks').update({ current_balance: nuevoSaldoBanco }).eq('id', form.bank_id);

        // 2. Registrar en Tabla Maestra 'movimientos_financieros'
        const { data: financialMovement, error: financialMovementError } = await supabase.from('movimientos_financieros').insert([{
            organization_id: orgId,
            bank_id: form.bank_id,
            sitio_id: form.sitio_id,
            tipo: 'Ingreso',
            categoria: 'Cobro',
            monto: monto,
            saldo_resultante: nuevoSaldoBanco,
            entidad_tipo: 'Cliente',
            entidad_id: form.cliente_id,
            descripcion: `Cobro: ${cli.full_name}. ${form.obs}`,
            fecha_operacion: buildLocalOperationTimestamp(form.fecha),
            user_id: profile?.user_id || null,
            is_active: true
        }]).select('id').single();

        if (financialMovementError) throw financialMovementError;

        // 3. Reducir Deuda del Cliente
        const { data: clientSnapshot, error: clientSnapshotError } = await fetchClientTimelineBalanceSnapshot({
          clientId: form.cliente_id,
          organizationId: orgId,
          supabaseClient: supabase,
        });
        if (clientSnapshotError) throw clientSnapshotError;

        const nuevoSaldoCli = getClientBalanceValue(clientSnapshot || cli) - monto;
        const { error: updateClientError } = await updateClientBalance({
          clientId: form.cliente_id,
          nextBalance: nuevoSaldoCli,
          supabaseClient: supabase,
        });
        if (updateClientError) throw updateClientError;

        // 4. Registrar en Cuenta Corriente del Cliente
        await supabase.from('client_movements').insert([{
            client_id: form.cliente_id, 
            organization_id: orgId, 
            type: 'Cobranza',
            amount: -monto, 
            current_balance: nuevoSaldoCli,
            observations: `Cobro Tesorería: ${banco.name}. ${form.obs}`
        }]);

        await applyCollectionToReceivables({
          organizationId: orgId,
          clientId: form.cliente_id,
          amount: monto,
          financialMovementId: financialMovement?.id,
        });

        setIsSuccess(true);
        toast.success("COBRANZA REGISTRADA");
        
        setTimeout(() => {
          setForm({ cliente_id: '', bank_id: '', sitio_id: '', amount: '', obs: '', fecha: today });
          setIsSuccess(false);
          setSaving(false);
          loadMasters();
          loadHistorial();
        }, 1500);

    } catch (e) { 
      toast.error("Error al procesar el ingreso");
      setSaving(false);
    }
  };

  // Mapea una factura/cuenta por cobrar al formato del modal de conciliación.
  // El campo de saldo pendiente en sales_receivables es pending_amount; el modal
  // y los handlers trabajan con open_amount, así que lo normalizamos.
  const mapReceivableToConciliationDoc = (receivable) => ({
    ...receivable,
    kindLabel: 'Factura',
    numberLabel: receivable.sale_number ? `N° ${receivable.sale_number}` : '',
    title: receivable.sale_number
      ? `Factura ${receivable.sale_number}`
      : (receivable.description || 'Comprobante de venta'),
    dateLabel: formatConciliationDate(receivable.sale_created_at || receivable.created_at),
    paymentConditionLabel: receivable.payment_condition_label || '',
    dueLabel: formatConciliationDate(receivable.payment_due_date || receivable.due_date),
    description: receivable.description || '',
  });

  const guardarCobro = async (e) => {
    e.preventDefault();
    if (!form.cliente_id || !form.bank_id || !form.sitio_id || !form.amount) {
      return toast.error('Complete todos los campos obligatorios');
    }

    const monto = parseLocalizedNumber(form.amount);
    if (!monto || monto <= 0) {
      return toast.error('Ingresá un monto válido');
    }

    const submission = {
      cliente_id: form.cliente_id,
      bank_id: form.bank_id,
      sitio_id: form.sitio_id,
      fecha: form.fecha,
      obs: form.obs,
      monto,
    };

    setPreparingConciliation(true);
    try {
      // Buscar facturas/cuotas pendientes del cliente para ofrecer conciliación.
      const { data: openReceivables, error: openReceivablesError } = await fetchOpenClientReceivables({
        organizationId: profile.organization_id,
        clientId: form.cliente_id,
      });
      if (openReceivablesError) throw openReceivablesError;

      // Sin facturas pendientes: se guarda directo como cobro a cuenta.
      if (!openReceivables || openReceivables.length === 0) {
        setPreparingConciliation(false);
        await persistCobro({ submission, allocations: [] });
        return;
      }

      // Con facturas pendientes: abrir modal con asignaciones sugeridas (vencimiento más próximo primero).
      const docsWithOpenAmount = openReceivables.map((receivable) => ({
        ...receivable,
        open_amount: Math.max(0, normalizeMoney(receivable.pending_amount)),
      }));
      const totalOpen = docsWithOpenAmount.reduce(
        (acc, receivable) => acc + receivable.open_amount,
        0,
      );
      const suggestedDocs = buildSuggestedAllocations(docsWithOpenAmount, monto).map(
        mapReceivableToConciliationDoc,
      );

      setConciliationDocs(suggestedDocs);
      setConciliationContext({
        submission,
        // El excedente sobre lo adeudado queda a favor del cliente: solo
        // exigimos cubrir lo que las facturas pueden absorber.
        requiredAllocation: Math.min(monto, totalOpen),
      });
      setConciliationOpen(true);
    } catch (err) {
      console.error(err);
      toast.error('No se pudieron cargar las facturas pendientes');
    } finally {
      setPreparingConciliation(false);
    }
  };

  const confirmarCobroConciliado = async () => {
    if (!conciliationContext?.submission) return;
    const assignedAmount = sumAllocationAmounts(conciliationDocs);
    if (assignedAmount + 0.000001 < Number(conciliationContext.requiredAllocation || 0)) {
      return toast.error('Todavía falta asignar parte del cobro');
    }

    await persistCobro({
      submission: conciliationContext.submission,
      allocations: conciliationDocs,
    });
  };

  const handleImprimirRecibo = (mov) => {
    toast.success("Generando recibo PDF...");
    // Aquí podrías integrar con una función de jsPDF o similar
    console.log("Imprimiendo recibo para:", mov);
  };

  const historialFiltrado = useMemo(() => {
    if (!filtroSitio) return historial;
    return historial.filter(h => h.sitio_id === filtroSitio);
  }, [historial, filtroSitio]);

  // KPI CALCULADOS
  const kpis = useMemo(() => {
    const active = historialFiltrado.filter(h => h.is_active);
    const total = active.reduce((acc, curr) => acc + Number(curr.monto), 0);
    const count = active.length;
    return { total, count };
  }, [historialFiltrado]);

  if (loading && historial.length === 0) return (
    <div className="imp-page imp-container">
        <div className="h-full flex flex-col items-center justify-center p-8">
            <div className="w-11 h-11 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center mb-3">
                <RefreshCcw className="text-slate-300 animate-spin" size={18} />
            </div>
            <p className="font-black uppercase tracking-[0.28em] text-slate-400 text-[10px]">Sincronizando Libro Diario...</p>
        </div>
    </div>
  );

  return (
    <div className="imp-page imp-container animate-nova-in">
      
      {/* HEADER ESTILO PRODUCTOS */}
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <ArrowDownCircle size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Caja y Cobranzas</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Ingreso unificado de fondos y gestión de deuda.
            </p>
          </div>
        </div>
      </div>

      {/* KPI SECTION */}
      <div className="imp-kpis mb-3">
        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Total Ingresos</p>
            <DollarSign size={16} className="text-emerald-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">
             ${kpis.total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">En periodo seleccionado</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Operaciones</p>
            <Activity size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.count}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Transacciones</p>
        </div>

        <div className="imp-kpi">
            <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Periodo</p>
                <Calendar size={16} className="text-slate-300" />
            </div>
            <p className="mt-1.5 text-xl font-black text-slate-800 tracking-tight truncate">
                {fechaDesde === fechaHasta ? 'Hoy' : 'Rango'}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Filtro activo</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        
        {/* PANEL DE REGISTRO - ESTILO CARD */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {/* CABECERA NEGRA ESTILO PRODUCTOS */}
            <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ArrowDownCircle size={16} className="text-[#3FF2AA]" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">
                  Nuevo Recibo
                </span>
              </div>
              <div className="text-[9px] font-bold uppercase tracking-widest text-white/60">
                Manual
              </div>
            </div>

            <div className="p-4">
              <form onSubmit={guardarCobro} className="space-y-3">
                <div className="space-y-1">
                  <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Cliente / Deudor</label>
                  <select 
                    className="w-full bg-slate-50 border border-slate-100 p-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner" 
                    value={form.cliente_id} onChange={e=>setForm({...form, cliente_id:e.target.value})} required
                  >
                    <option value="">SELECCIONE CLIENTE...</option>
                    {clientes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.full_name} (Deuda: {formatCurrencyAr(getClientBalanceValue(c))})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Sucursal (Imputación)</label>
                  <select 
                    className="w-full bg-slate-50 border border-slate-100 p-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner" 
                    value={form.sitio_id} onChange={e=>setForm({...form, sitio_id:e.target.value})} required
                  >
                    <option value="">CENTRO DE COSTOS...</option>
                    {sitios.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Cuenta Destino</label>
                  <select 
                    className="w-full bg-slate-50 border border-slate-100 p-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner" 
                    value={form.bank_id} onChange={e=>setForm({...form, bank_id:e.target.value})} required
                  >
                    <option value="">SELECCIONE CUENTA...</option>
                    {bancos.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name} (Disp: {formatCurrencyAr(b.current_balance || 0)})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Monto Cobrado</label>
                    <LocalizedNumberInput
                      required
                      decimals={2}
                      selectOnFocus
                      className="w-full bg-slate-50 border border-slate-100 p-2.5 rounded-xl text-[11px] font-black text-emerald-600 outline-none shadow-inner"
                      value={form.amount}
                      onValueChange={(display) => setForm({ ...form, amount: display })}
                      placeholder="0,00"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Fecha Operación</label>
                    <input type="date" 
                      className="w-full bg-slate-50 border border-slate-100 p-2.5 rounded-xl text-[10px] font-bold outline-none uppercase" 
                      value={form.fecha} onChange={e=>setForm({...form, fecha:e.target.value})} />
                  </div>
                </div>

                <textarea 
                  placeholder="DETALLE OPERATIVO / OBSERVACIONES..." 
                  className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] h-20 shadow-inner resize-none"
                  value={form.obs} onChange={e => setForm({...form, obs: e.target.value})} 
                />

                <button type="submit" disabled={saving || isSuccess || preparingConciliation} className={`w-full py-4 rounded-xl font-black text-[11px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-lg ${isSuccess ? 'bg-[#3ff2aa] text-[#0B2A36]' : 'bg-[#3ff2aa] text-[#0B2A36] hover:scale-[1.02] active:scale-95 shadow-emerald-50'}`}>
                  {isSuccess ? <><Check size={18} strokeWidth={4} className="animate-in zoom-in" /> Cobro Registrado</> : (saving || preparingConciliation) ? <><RefreshCcw size={18} className="animate-spin" /> {preparingConciliation ? 'Buscando facturas...' : 'Procesando...'}</> : 'Confirmar Cobro'}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* AUDITORÍA CON FILTRO Y BOTÓN DE IMPRESIÓN */}
        <div className="lg:col-span-2 space-y-4">
          {/* FILTROS MAESTROS - ESTILO FILTROS PRODUCTOS */}
          <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-end gap-3">
            <div className="flex-[1.5] space-y-1 w-full">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Sucursal</label>
              <select value={filtroSitio} onChange={e => setFiltroSitio(e.target.value)} className="w-full bg-slate-50 border border-slate-100 p-2.5 rounded-xl text-[10px] font-bold outline-none cursor-pointer">
                <option value="">TODAS LAS SUCURSALES</option>
                {sitios.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
              </select>
            </div>
            <div className="flex-1 space-y-1 w-full">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Desde</label>
              <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} className="w-full bg-slate-50 border border-slate-100 p-2.5 rounded-xl text-[10px] font-bold outline-none uppercase" />
            </div>
            <div className="flex-1 space-y-1 w-full">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Hasta</label>
              <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} className="w-full bg-slate-50 border border-slate-100 p-2.5 rounded-xl text-[10px] font-bold outline-none uppercase" />
            </div>
            
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setMostrarEliminados(!mostrarEliminados)}
                className={`p-2.5 rounded-xl border transition-all ${mostrarEliminados ? 'bg-rose-50 text-rose-500 border-rose-200' : 'bg-slate-50 text-slate-400 border-slate-100 hover:text-slate-600'}`}
                title={mostrarEliminados ? "Ocultar anulados" : "Mostrar anulados"}
              >
                {mostrarEliminados ? <EyeOff size={18}/> : <Eye size={18}/>}
              </button>

              <button onClick={loadHistorial} className="p-2.5 bg-[#0B2A36] text-white rounded-xl hover:bg-slate-800 shadow-sm transition-all border border-[#0B2A36]">
                <Search size={18} />
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col min-h-[450px]">
            <div className="overflow-x-auto imp-table-wrap">
                <table className="w-full text-left">
                <thead>
                    <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                    <th className="px-6 py-3 pl-6">Fecha / Hora</th>
                    <th className="px-6 py-3">Cuenta Destino</th>
                    <th className="px-6 py-3 text-right pr-6">Monto Neto</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                    {historialFiltrado.length === 0 ? (
                        <tr>
                            <td colSpan={3} className="py-12 text-center text-slate-300">
                                <p className="text-[9px] font-black uppercase tracking-[0.2em]">Sin operaciones en el periodo</p>
                            </td>
                        </tr>
                    ) : (
                        historialFiltrado.map(h => (
                        <React.Fragment key={h.id}>
                            <tr 
                            onClick={() => setExpandedId(expandedId === h.id ? null : h.id)} 
                            className={`transition-colors cursor-pointer group ${!h.is_active ? 'bg-rose-50/40 hover:bg-rose-50/60' : 'hover:bg-slate-50/50'}`}
                            >
                            <td className="px-6 py-4 pl-6 text-[10px] font-bold text-slate-500">
                                <div className="flex items-center gap-2">
                                    <Calendar size={12} className="text-slate-300" />
                                    {new Date(h.fecha_operacion).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                    {!h.is_active && <span className="ml-2 px-1.5 py-0.5 bg-rose-100 text-rose-600 text-[8px] font-black rounded uppercase border border-rose-200">Anulado</span>}
                                </div>
                            </td>
                            <td className={`px-6 py-4 text-[10px] font-black text-slate-700 uppercase tracking-tight ${!h.is_active ? 'line-through opacity-50' : ''}`}>
                                {h.banks?.name}
                            </td>
                            <td className="px-6 py-4 pr-6 text-right">
                                <span className={`font-black text-[11px] ${!h.is_active ? 'text-slate-400 line-through' : 'text-emerald-600'}`}>
                                +${h.monto.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                </span>
                            </td>
                            </tr>
                            {expandedId === h.id && (
                            <tr className={`${!h.is_active ? 'bg-rose-50/40' : 'bg-slate-50/50'} animate-in fade-in slide-in-from-top-1`}>
                                <td colSpan="3" className="p-4 px-10">
                                <div className={`bg-white border rounded-2xl p-5 shadow-inner space-y-3 relative ${!h.is_active ? 'border-rose-100 opacity-80' : 'border-slate-100'}`}>
                                    
                                    {/* CABECERA DE ACCIONES INLINE */}
                                    {h.is_active ? (
                                    <div className="flex justify-between items-start">
                                        {/* IZQUIERDA: TITULO */}
                                        <div className="flex items-center gap-2 text-[9px] font-black text-slate-400 uppercase tracking-widest mt-1">
                                        <FileText size={14}/> Auditoría de Transacción
                                        </div>
                                        
                                        {/* DERECHA: BOTONES */}
                                        <div className="flex items-center gap-2">
                                        {/* BOTÓN IMPRIMIR */}
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); handleImprimirRecibo(h); }}
                                            className="flex items-center gap-2 bg-[#51A7FD] text-white px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-md"
                                        >
                                            <Printer size={14} /> Imprimir Recibo
                                        </button>

                                        {/* BOTON DE ANULAR / CONFIRMAR */}
                                        {confirmDeleteId === h.id ? (
                                            <div className="flex items-center gap-2 animate-in zoom-in duration-200 bg-white p-1 rounded-xl shadow-sm border border-rose-100">
                                            <span className="text-[9px] font-black text-rose-500 uppercase px-2">¿Anular?</span>
                                            <button 
                                                onClick={(e) => { e.stopPropagation(); anularMovimiento(h); }}
                                                disabled={processingDeleteId === h.id}
                                                className="p-1.5 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors"
                                            >
                                                {processingDeleteId === h.id ? <RefreshCcw size={14} className="animate-spin"/> : <Check size={14} strokeWidth={3}/>}
                                            </button>
                                            <button 
                                                onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                                                className="p-1.5 bg-slate-100 text-slate-500 rounded-lg hover:bg-slate-200 transition-colors"
                                            >
                                                <X size={14} strokeWidth={3}/>
                                            </button>
                                            </div>
                                        ) : (
                                            <button 
                                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(h.id); }}
                                            className="flex items-center gap-2 px-4 py-2 bg-white text-rose-500 hover:bg-rose-50 hover:text-rose-600 rounded-xl border border-rose-100 transition-all shadow-sm"
                                            >
                                            <Trash2 size={14} />
                                            <span className="text-[9px] font-black uppercase tracking-widest">Anular</span>
                                            </button>
                                        )}
                                        </div>
                                    </div>
                                    ) : (
                                        // SI ESTÁ ANULADO: SOLO MUESTRA TEXTO ROJO
                                        <div className="flex items-center gap-2 text-[9px] font-black text-rose-500 uppercase tracking-widest p-2 bg-rose-50 rounded-xl border border-rose-100 w-fit">
                                        <AlertCircle size={14}/> Operación Anulada - Registro Histórico
                                        </div>
                                    )}

                                    <div className="p-4 bg-slate-50/50 rounded-xl border border-slate-100">
                                        <p className="text-[10px] font-black text-slate-700 uppercase leading-relaxed">
                                            {h.descripcion}
                                        </p>
                                    </div>
                                    
                                    <div className="flex justify-between items-center pt-2">
                                        <div className="flex flex-col">
                                            <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Sucursal Imputada</span>
                                            <span className="text-[10px] font-bold text-blue-600 uppercase tracking-tight">{h.sitios?.nombre || 'Administración Central'}</span>
                                        </div>
                                        <div className="flex flex-col items-end">
                                            <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Saldo Resultante Banco</span>
                                            <span className="text-[10px] font-black text-slate-600">${h.saldo_resultante?.toLocaleString()}</span>
                                        </div>
                                    </div>
                                </div>
                                </td>
                            </tr>
                            )}
                        </React.Fragment>
                        ))
                    )}
                </tbody>
                </table>
            </div>
          </div>
        </div>
      </div>

      <AccountReconciliationModal
        open={conciliationOpen}
        title="Conciliar cobro con facturas"
        subtitle={clientes.find((c) => c.id === conciliationContext?.submission?.cliente_id)?.full_name || ''}
        documents={conciliationDocs}
        totalAmount={Number(conciliationContext?.submission?.monto || 0)}
        requiredAllocation={Number(conciliationContext?.requiredAllocation || 0)}
        onAllocationChange={handleConciliationAllocationChange}
        onClose={() => closeConciliationModal()}
        onConfirm={confirmarCobroConciliado}
        confirmLabel="Confirmar cobro"
        loading={saving}
        helperText="Asigná el cobro a una o más facturas pendientes. El excedente queda a favor del cliente."
        emptyMessage="No hay facturas pendientes para conciliar."
      />

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .imp-table-wrap { width: 100%; overflow-x: auto; }
      `}</style>
    </div>
  );
}

export default TesoreriaCobranzas;
